'use client';

import { useEffect, useRef, useCallback } from 'react';
import { useScheduler } from '../SchedulerContext';
import { useStepNavigation } from '../StepNavigationContext';
import { saveDraftQuote } from './useAutoSaveDraft';
import { createChargeObjects } from '@/util/functions/quote/createChargeObjects';

/**
 * Orchestrates automatic draft saving throughout the scheduler flow.
 *
 * Features:
 * - Auto-saves on forward step transitions
 * - Debounced auto-save for critical fields (contact email, address)
 * - Centralizes all draft save logic - child components don't need to know about drafts
 *
 * Only saves when:
 * - User has provided contact email OR
 * - A draft quote already exists (draftQuoteId is set)
 */
export function useDraftSaveOrchestrator() {
  const { activeStep, direction } = useStepNavigation();

  const {
    // Contact info
    contact,
    selectedRole,
    additionalContacts,

    // Service selections
    selectedPrimaryService,
    selectedBundle,
    selectedAddons,

    // Property info
    address,
    requiredInfoValues,
    equipmentQuantities,
    closingDate,

    // Pricing & Services
    priceData,
    finalServices,

    // Bundle data
    bundles,

    // Draft state
    draftQuoteId,
    setDraftQuoteId,
    setDraftQuoteSlug,
    isCheckingDrafts,

    // Escape hatch state
    escapeHatchReason,

    // Auth tokens
    jwtToken,
    companyId,
    isCreatingNewQuote,
    
    // WhatConverts lead ID
    wcLeadId,
  } = useScheduler();

  const hasContactInfo = Boolean(contact?._id || contact?.email);
  const hasServiceSelection = Boolean(
    selectedPrimaryService || finalServices.length > 0
  );
  const hasViableDraftData = hasContactInfo && hasServiceSelection;

  // Track previous step for transition detection
  const prevStepRef = useRef(activeStep);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isSavingRef = useRef(false);

  // Centralized save function that gathers all current state
  const performSave = useCallback(
    async (reason: string) => {
      // Don't save if we're currently checking for existing drafts
      // This prevents race condition where draft is created before check completes
      if (isCheckingDrafts) {
        console.log(
          `[Draft Auto-Save] Skipping save (${reason}): Draft check in progress`
        );
        return;
      }

      // Don't save if we're currently creating a new quote
      // This prevents race condition where multiple quotes are created during startNewQuote
      // MUST be checked before checking draftQuoteId to prevent creating new drafts
      // when draftQuoteId is cleared but new quote hasn't been created yet
      if (isCreatingNewQuote) {
        console.log(`[Draft Auto-Save] Skipping save (${reason}): New quote creation in progress`);
        return;
      }

      // Only save if we have minimum viable data or existing draft
      if (!hasViableDraftData && !draftQuoteId) {
        console.log(
          `[Draft Auto-Save] Skipping save (${reason}): Missing contact or service selection`
        );
        return;
      }

      // Prevent concurrent saves
      if (isSavingRef.current) {
        console.log(
          `[Draft Auto-Save] Skipping save (${reason}): Save already in progress`
        );
        return;
      }

      try {
        isSavingRef.current = true;
        console.log(`[Draft Auto-Save] Saving draft (${reason})...`);

        // Transform AddressForScheduler to PropertyType for backend
        // Frontend uses AddressForScheduler with an "address" field (full formatted string)
        // Backend expects PropertyType without the "address" field
        // This transformation removes the redundant "address" field before sending
        const propertyData = address
          ? {
              street: address.street,
              unit: address.unit,
              city: address.city,
              state: address.state,
              zip: address.zip,
              lat: address.lat,
              lng: address.lng,
              sqft: address.sqft,
              yearBuilt: address.yearBuilt,
              unitCount: address.unitCount,
              foundation: address.foundation,
              // Omitted when empty so the key drops out of the JSON body and
              // the update leaves any stored history alone.
              zestimates: address.zestimates?.length
                ? address.zestimates
                : undefined,
            }
          : undefined;

        // Build people array from primary contact + additional contacts
        // Backend expects: [{ _contactId, _roleId, primary }]
        const peopleArray = [];

        // Add primary contact if available
        if (contact?._id && selectedRole?._id) {
          peopleArray.push({
            _contactId: contact._id,
            _roleId: selectedRole._id,
            primary: true,
          });
        }

        // Add additional contacts
        if (additionalContacts && additionalContacts.length > 0) {
          additionalContacts.forEach((addContact) => {
            if (addContact._contactId?._id && addContact._roleId) {
              peopleArray.push({
                _contactId: addContact._contactId._id,
                _roleId: addContact._roleId,
                primary: addContact.primary || false,
              });
            }
          });
        }

        // Create charge objects from finalServices (mirrors QuoteModal logic)
        const chargeObjects =
          finalServices.length > 0
            ? createChargeObjects(finalServices)
            : undefined;

        const draftResponse = await saveDraftQuote({
          email: contact?.email,
          firstName: contact?.firstName,
          lastName: contact?.lastName,
          phone: contact?.phone,
          contactId: contact?._id,
          selectedRoleId: selectedRole?._id,
          people: peopleArray.length > 0 ? peopleArray : undefined,
          property: propertyData,
          requiredInfoValues:
            requiredInfoValues.length > 0 ? requiredInfoValues : undefined,
          equipmentQuantities:
            equipmentQuantities.length > 0 ? equipmentQuantities : undefined,
          // `null` clears; an omitted key would leave the old date stored.
          closingDate: closingDate || null,
          duration: priceData?.totalDuration,
          total: priceData?.totalPrice,
          // Service selections (stored separately + in message field by backend)
          selectedPrimaryServiceId: selectedPrimaryService || undefined,
          selectedBundleId: selectedBundle || undefined,
          selectedAddonIds:
            selectedAddons.length > 0 ? selectedAddons : undefined,
          // Charge objects (NEW - mirrors QuoteModal logic)
          charges: chargeObjects,
          // Pass current draft ID so backend knows which draft to update (or create new if null)
          currentDraftId: draftQuoteId || undefined,
          // Set schedulingBlockedOnline to the escape hatch reason (or null if allowed)
          schedulingBlockedOnline: escapeHatchReason,
          // WhatConverts lead ID
          wcLeadId,
          // Auth tokens (required)
          jwtToken,
          companyId,
        });

        if (draftResponse) {
          // Update draft ID and slug in context if not already set
          if (!draftQuoteId) {
            setDraftQuoteId(draftResponse._id);
            setDraftQuoteSlug(draftResponse.slug);
          }
        }
      } catch (error) {
        console.error(`[Draft Auto-Save] Failed to save (${reason}):`, error);
      } finally {
        isSavingRef.current = false;
      }
    },
    [
      contact,
      hasViableDraftData,
      selectedRole,
      additionalContacts,
      selectedPrimaryService,
      selectedBundle,
      selectedAddons,
      address,
      requiredInfoValues,
      equipmentQuantities,
      closingDate,
      priceData,
      finalServices,
      bundles,
      draftQuoteId,
      setDraftQuoteId,
      setDraftQuoteSlug,
      isCheckingDrafts,
      isCreatingNewQuote,
      escapeHatchReason,
      wcLeadId,
      jwtToken,
      companyId,
    ]
  );

  // Auto-save on forward step transitions
  useEffect(() => {
    const prevStep = prevStepRef.current;
    const currentStep = activeStep;

    // Save when moving forward to a new step
    if (direction === 'forward' && currentStep > prevStep) {
      console.log(
        `[Draft Auto-Save] Step transition detected: ${prevStep} -> ${currentStep}`
      );
      performSave(`step transition ${prevStep} -> ${currentStep}`);
    }

    // Update previous step reference
    prevStepRef.current = currentStep;
  }, [activeStep, direction, performSave]);

  // Debounced auto-save for critical field changes
  useEffect(() => {
    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Only debounce if we have contact email (initial capture) or existing draft
    // This matches the logic in performSave: save if hasViableDraftData OR draftQuoteId exists
    if (hasViableDraftData || draftQuoteId) {
      debounceTimerRef.current = setTimeout(() => {
        performSave('debounced field change');
      }, 4000); // 4 second debounce
    } else if (debounceTimerRef.current) {
      debounceTimerRef.current = null;
    }

    // Cleanup timer on unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [
    // Critical fields that should trigger debounced save
    contact?.email,
    contact?.firstName,
    contact?.lastName,
    contact?.phone,
    additionalContacts,
    selectedPrimaryService,
    selectedBundle,
    selectedAddons,
    address?.address,
    address?.street, // Also watch for address changes
    address?.city,
    address?.state,
    address?.zip,
    requiredInfoValues, // Watch for property info changes
    equipmentQuantities, // Watch for equipment changes
    finalServices, // Trigger save when services/pricing changes
    draftQuoteId,
    isCheckingDrafts, // Reset timer when checking status changes
    escapeHatchReason, // Trigger save when escape hatch state changes
    hasViableDraftData,
    performSave,
  ]);

  // Cancel debounce function to prevent race conditions
  const cancelDebounce = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  // Return manual save function and cancel debounce function
  return {
    manualSave: performSave,
    cancelDebounce,
  };
}
